import { Scribbles } from "@somethings-ui/core/assets";
import { Image as ImageIcon } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";
import type { ActivityToolId, ContentToolId } from "./tool-config";

function maskStyle(src: string): CSSProperties {
  return {
    WebkitMaskImage: `url(${src})`,
    maskImage: `url(${src})`,
    WebkitMaskSize: "100% 100%",
    maskSize: "100% 100%",
    WebkitMaskRepeat: "no-repeat",
    maskRepeat: "no-repeat",
  };
}

interface ActivityCard {
  cardStyle: CSSProperties;
  colorStyle: CSSProperties;
  offset: CSSProperties;
  label: string;
}

const ACTIVITY_CARD: Record<ActivityToolId, ActivityCard> = {
  hot_takes: {
    cardStyle: {
      backgroundImage: "linear-gradient(180deg, #322964 0%, #1D1F46 100%)",
    },
    colorStyle: {
      backgroundImage: "linear-gradient(180deg, #B164FF 0%, #51237F 100%)",
    },
    offset: { left: "-9.34px", top: "-13.7px" },
    label: "Hot Takes",
  },
  connect_moment: {
    cardStyle: { backgroundColor: "#2F2D82" },
    colorStyle: { backgroundColor: "#7178ff" },
    offset: { left: "-8.58px", top: "-12.82px" },
    label: "Connect",
  },
  deep_thoughts: {
    cardStyle: { backgroundColor: "#582020" },
    colorStyle: { backgroundColor: "#ff9812" },
    offset: { left: "-8.58px", top: "-12.82px" },
    label: "Deep",
  },
};

export function hasActivityToolThumbnail(
  id: string,
): id is ActivityToolId {
  return Object.prototype.hasOwnProperty.call(ACTIVITY_CARD, id);
}

export function ActivityToolThumbnail({ id }: { id: ActivityToolId }) {
  const card = ACTIVITY_CARD[id];
  return (
    <ArtBox>
      <div
        className="relative flex h-[33.51px] w-[22.485px] rotate-[7deg] items-center justify-center overflow-hidden rounded-[1.5px] border-[0.5px] border-surfaces-divider shadow-[0px_0px_1px_0px_rgba(0,0,0,0.08)]"
        style={card.cardStyle}
        aria-hidden
      >
        <div
          className="absolute z-0 flex h-[38.056px] w-[43.08px] items-center justify-center"
          style={card.offset}
        >
          <div className="rotate-[22.47deg]">
            <div
              className="h-[26.418px] w-[35.692px]"
              style={{ ...card.colorStyle, ...maskStyle(Scribbles.purpleCloud) }}
            />
          </div>
        </div>
        <span className="relative z-10 text-[3.945px] font-bold leading-[1.15] tracking-[-0.04px] text-white">
          {card.label}
        </span>
      </div>
    </ArtBox>
  );
}

export function ContentToolThumbnail({ id }: { id: ContentToolId }) {
  switch (id) {
    case "swap":
      return (
        <ArtBox>
          <div
            className="absolute left-1/2 top-1/2 size-[30.6px] -translate-x-1/2 -translate-y-1/2 rounded-full"
            style={{
              background:
                "radial-gradient(closest-side, #d9ccf5 0%, rgba(217,204,245,0) 100%)",
            }}
          />
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rotate-[-12.69deg]">
            <div
              className="size-[32.7px]"
              style={{
                backgroundColor: "#ffe81f",
                ...maskStyle(Scribbles.yellowSpiral),
              }}
            />
          </div>
          <PhotoCard className="left-[4px] top-[2px] -rotate-[13.85deg]" />
          <PhotoCard className="left-[10px] top-[5px] rotate-[3.98deg]" />
        </ArtBox>
      );
    case "moment":
      return (
        <ArtBox>
          <div className="absolute left-[8px] top-[3px] h-[30px] w-[21px] -rotate-[6.5deg] rounded-[1.5px] border-[0.5px] border-[#e2ddd4] bg-[#ece8e1]" />
          <div className="absolute left-[7px] top-[3px] flex h-[30px] w-[21px] rotate-[9deg] items-center justify-center rounded-[1.5px] border-[0.5px] border-[#e2ddd4] bg-[#ece8e1]">
            <ImageIcon className="size-[7px] text-[#b8b0a3]" strokeWidth={1.75} />
          </div>
        </ArtBox>
      );
    case "note":
      return (
        <ArtBox>
          <div className="relative flex h-[34px] w-[24px] -rotate-[4deg] flex-col gap-[1px] overflow-hidden rounded-[1.5px] border-[0.5px] border-[#efece6] bg-[#f7f5f1] p-[3px] shadow-[0px_0.3px_1.3px_0px_rgba(0,0,0,0.08)]">
            <div className="absolute left-[2px] top-[2px] size-[3px] rounded-full bg-stone-300" />
            <div className="mt-[5px] flex flex-col gap-[1px]">
              {NOTE_TEXT_LINE_WIDTHS.map((width, i) => (
                <div
                  key={i}
                  style={{ width }}
                  className="h-[1px] rounded-full bg-stone-300/70"
                />
              ))}
            </div>
          </div>
        </ArtBox>
      );
    default: {
      const _exhaustive: never = id;
      return _exhaustive;
    }
  }
}

function PhotoCard({ className }: { className: string }) {
  return (
    <div
      className={`absolute h-[26px] w-[18px] overflow-hidden rounded-[1.5px] border-[0.5px] border-white bg-white/50 shadow-[0px_0.3px_2.6px_0px_rgba(0,0,0,0.06)] backdrop-blur-[0.5px] ${className}`}
    >
      <div className="absolute left-[1.5px] top-[1.5px] size-[3.5px] rounded-full bg-zinc-300/90" />
    </div>
  );
}

// Decorative ruled lines faking text on the note-paper thumbnail; varied
// widths so the last line of each "paragraph" looks short.
const NOTE_TEXT_LINE_WIDTHS = [
  "100%",
  "100%",
  "80%",
  "100%",
  "100%",
  "60%",
  "100%",
  "100%",
  "66%",
] as const;

function ArtBox({ children }: { children: ReactNode }) {
  return (
    <div className="relative flex size-9 shrink-0 items-center justify-center overflow-hidden">
      {children}
    </div>
  );
}
